import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";

const navLinks = [
  { label: "Home", path: "/" },
  { label: "Services", path: "/services" },
  { label: "About", path: "/about" },
  { label: "Gallery", path: "/gallery" },
  { label: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white shadow-[0_2px_20px_rgba(0,0,0,0.08)]"
          : "backdrop-blur-xl bg-white/90"
      } border-b border-[#E2E8DF]`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0">
            <img
              src="/logo.svg"
              alt="Absolute Landscapers"
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  location.pathname === link.path
                    ? "text-[#3a5c2e] bg-[#F0F4EF]"
                    : "text-[#4A5D48] hover:text-[#3a5c2e] hover:bg-[#F0F4EF]"
                }`}
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="tel:+16472422860"
              data-testid="nav-phone"
              className="flex items-center gap-2 text-[#3a5c2e] text-sm font-medium hover:text-[#2d4a24] transition-colors"
            >
              <Phone size={15} />
              <span style={{ fontFamily: "'DM Sans', sans-serif" }}>647.242.2860</span>
            </a>
            <Link
              to="/contact"
              data-testid="nav-cta"
              className="btn-primary px-5 py-2.5 rounded-lg text-sm"
            >
              Get a Free Quote
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden p-2 rounded-lg text-[#3a5c2e] hover:bg-[#F0F4EF] transition-colors"
            aria-label="Toggle menu"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-white border-t border-[#E2E8DF] shadow-lg"
        >
          <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === link.path
                    ? "text-[#3a5c2e] bg-[#F0F4EF]"
                    : "text-[#4A5D48] hover:bg-[#F0F4EF] hover:text-[#3a5c2e]"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-3 border-t border-[#E2E8DF] flex flex-col gap-2">
              <a
                href="tel:+16472422860"
                className="flex items-center gap-2 px-4 py-2.5 text-[#3a5c2e] text-sm font-medium"
              >
                <Phone size={15} /> 647.242.2860
              </a>
              <Link
                to="/contact"
                data-testid="mobile-cta"
                className="btn-primary text-center px-4 py-3 rounded-lg text-sm"
              >
                Get a Free Quote
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
